/**
 * Theme management service.
 *
 * Handles theme CSS loading, theme switching, and propagation
 * of theme changes to both legacy DOM elements and React components.
 */

import { THEME_DAY_COOKIE_VALUE } from '@/utils/theme';

// ── Theme application ─────────────────────────────────────────────

export function applyLegacyThemes(themeColor?: string): void {
  const theme = themeColor ?? localStorage.getItem("OrnaThemeColor") ?? "";
  const themeMap: Record<string, string> = {
    '': '',
    'kendo.customGrey': 'theme-gray-dark',
    'kendo.custom': 'theme-blue-dark'
  };
  const themeClass = themeMap[theme] || '';

  document.querySelectorAll(
    '.officers-assigned, .vehicles-assigned, .people-assigned, ' +
    '.mx-datepicker-popup, ' +
    '[data-kstc-modal], [data-kstm-modal], ' +
    '.case-report-officers-assigned, .responding-agencies, ' +
    '.notifications-assigned, .client-group-sites, ' +
    '.officer-toolbar, .kst-rich-text-editor, ' +
    '.citations-screen, .citation-form, .permissions-screen, ' +
    '.notes-screen, .citations-grid, ' +
    '.kst-data-grid'
  ).forEach((el: Element) => {
    (el as HTMLElement).classList.remove('theme-gray-dark', 'theme-blue-dark');
    if (themeClass) (el as HTMLElement).classList.add(themeClass);
  });

  window.dispatchEvent(new CustomEvent('vue2-theme-change', {
    detail: { theme: themeClass }
  }));
}

// ── Theme color management ────────────────────────────────────────

// Swaps the theme stylesheet link only — does not touch localStorage.
export function applyThemeStylesheet(themeColor: string): void {
  let themeLink = document.querySelector('link[title=styles-animations]') as HTMLLinkElement;

  if (!themeLink) {
    themeLink = document.createElement('link');
    themeLink.rel = 'stylesheet';
    themeLink.type = 'text/css';
    themeLink.title = 'styles-animations';
    document.head.appendChild(themeLink);
  }

  const themeFileMap: Record<string, string> = {
    '': '/Content/app.css',
    'kendo.custom': '/Content/theme-blue-dark.css',
    'kendo.customGrey': '/Content/theme-gray-dark.css'
  };

  themeLink.href = themeFileMap[themeColor] || '/Content/app.css';
}

export function updateThemeColor(newThemeColor: string): void {
  localStorage.setItem("OrnaThemeColor", newThemeColor);

  applyThemeStylesheet(newThemeColor);

  applyLegacyThemes(newThemeColor);
}

export function getCurrentThemeColor(): string {
  const themeColor = localStorage.getItem("OrnaThemeColor");
  if (themeColor && themeColor.length > 0) {
    return themeColor;
  } else {
    return THEME_DAY_COOKIE_VALUE;
  }
}

// ── Register on window ────────────────────────────────────────────

(window as any).applyLegacyThemes = applyLegacyThemes;
(window as any).updateThemeColor = updateThemeColor;
(window as any).getCurrentThemeColor = getCurrentThemeColor;
